import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addNote,
  createContactSource,
  deleteContactSource,
  getContact,
  listContacts,
  listContactSources,
  patchContact,
  renameContactSource,
  type PatchContactBody,
} from '../api/contacts';
import type { ContactListFilters } from '../api/types';

export function useContactList(filters: ContactListFilters) {
  return useQuery({
    queryKey: ['contacts', 'list', filters],
    queryFn: () => listContacts(filters),
    refetchOnWindowFocus: false,
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: ['contacts', 'detail', id],
    queryFn: () => getContact(id as string),
    enabled: Boolean(id),
  });
}

export function usePatchContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchContactBody }) => patchContact(id, body),
    onSuccess: (_res, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', id] });
    },
  });
}

export function useAddNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => addNote(id, text),
    onSuccess: (_res, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', id] });
    },
  });
}

/** Bulk-updates several contacts with the same PATCH body (owner reassignment, add-tag), one request per contact. */
export function useBulkPatchContacts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, body }: { ids: string[]; body: PatchContactBody }) => {
      const results = await Promise.allSettled(ids.map((id) => patchContact(id, body)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { total: ids.length, failed };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useContactSources() {
  return useQuery({ queryKey: ['contacts', 'sources'], queryFn: listContactSources });
}

export function useCreateContactSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => createContactSource(title),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts', 'sources'] }),
  });
}

export function useRenameContactSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => renameContactSource(id, title),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts', 'sources'] }),
  });
}

export function useDeleteContactSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteContactSource(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts', 'sources'] }),
  });
}
